import { Trans } from "react-i18next";

/**
 * Where a publication comes from: the journal it appeared in, or the congress
 * for a congress document ("In …"); the organisation behind a podcast
 * ("By …").
 */
export type PublicationSource = { kind: "in" | "by"; name: string };

/** A publication's journal, or a congress: "In …". */
export function journalSource(
  journal: { name: string } | null
): PublicationSource | null {
  return journal === null ? null : { kind: "in", name: journal.name };
}

/** A podcast, or one of its episodes, comes from its owner: "By …". */
export function podcastSource(
  owner: { name: string } | null
): PublicationSource | null {
  return owner === null ? null : { kind: "by", name: owner.name };
}

/**
 * The source line of a card, one sentence in every language: "In {name}" or
 * "By {name}", the name stressed. With `onNameClick`, the name opens the
 * source (a journal page); the click does not reach the card.
 */
export function SourceLabel({
  source,
  onNameClick,
}: {
  source: PublicationSource;
  onNameClick: (() => void) | null;
}): React.JSX.Element {
  const name =
    onNameClick === null ? (
      <span className="font-semibold text-ds-primary" />
    ) : (
      <span
        role="link"
        tabIndex={0}
        className="cursor-pointer font-semibold text-ds-primary hover:underline"
        onClick={(event) => {
          event.stopPropagation();
          onNameClick();
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          event.stopPropagation();
          onNameClick();
        }}
      />
    );
  return (
    <span className="font-normal text-ds-slate-600">
      {source.kind === "in" ? (
        <Trans
          i18nKey="In <name>{{name}}</name>"
          values={{ name: source.name }}
          components={{ name: name }}
        />
      ) : (
        <Trans
          i18nKey="By <name>{{name}}</name>"
          values={{ name: source.name }}
          components={{ name: name }}
        />
      )}
    </span>
  );
}
